import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormContext } from "react-hook-form";

const Communications = ({
    selectedCompany,
    fetchCompany
}) => {

    const {
        register,
        formState: { isDirty },
        setValue,
        reset,
        watch
    } = useFormContext();

    const [isLoading, setIsLoading] = useState(false);
    const [requireEmployeeID, setRequireEmployeeID] = useState(!!selectedCompany?.require_employee_id)
    const [saving, setSaving] = useState(false);
    const [operations_email, sms_functionality, email_functionality, app_download_reminders_functionality, automatic_deactivation_functionality] = watch(['operations_email', 'sms_functionality', 'email_functionality', 'app_download_reminders_functionality', 'automatic_deactivation_functionality']);
    const [late_cancellation_alert_enabled, post_cutoff_cancellation_alert_enabled] = watch(['late_cancellation_alert_enabled', 'post_cutoff_cancellation_alert_enabled']);
    // The cancellation cost alerts are Etapath-admin only; the server drops them for anyone else too (CU-86cbawn2a)
    const canManageCostAlerts = !!selectedCompany?.can_manage_cancellation_cost_alerts;
    const cutOffTimeLabel = selectedCompany?.cut_off_time ? ` (${selectedCompany.cut_off_time})` : '';

    useEffect(() => {
        register('operations_email');
        register('sms_functionality');
        register('email_functionality');
        register('app_download_reminders_functionality');
        register('automatic_deactivation_functionality');
        register('late_cancellation_alert_enabled');
        register('post_cutoff_cancellation_alert_enabled');
    }, [register]);

    useEffect(() => {
        setRequireEmployeeID(!!selectedCompany?.require_employee_id);
    }, [selectedCompany?.id, selectedCompany?.require_employee_id]);

    // Save changes
    const updateCompany = async () => {
        setIsLoading(true)
        try {
            await axiosInstance(`/companies/${selectedCompany.id}`, {
                method: 'PATCH',
                data: {
                    company: {
                        operations_email,
                        ...(canManageCostAlerts ? { late_cancellation_alert_enabled, post_cutoff_cancellation_alert_enabled } : {})
                    }
                },
                headers: { Accept: 'application/json' },
            });

            await fetchCompany();
        } catch (error) {
            console.error("Couldn't update company: ", error);
        }
        finally {
            setIsLoading(false)
        }
    };

    const handleChangeFunctionality = async (featureType) => {
        
        try {
            await axiosInstance({
                method: 'GET',
                url: `/companies/${selectedCompany.id}/features/${featureType}`,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            await fetchCompany();
        } catch (error) {
            console.error(`Error updating ${featureType}`, error);
        }
    };

    const handleRequireEmployeeID = async (nextValue) => {
        setSaving(true);
        const prev = requireEmployeeID;
        setRequireEmployeeID(nextValue);

        try {
            await axiosInstance.put(
            `/companies/${selectedCompany.id}/require_employee`,
            { require_employee_id: nextValue },
            { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
            );

        } catch (err) {
            setRequireEmployeeID(prev);
            console.error('Error updating require_employee_id', err);
        } finally {
            setSaving(false);
        }
    };

    // Revert unsaved changes
    const handleRevertChanges = () => {
        reset();
    };

    // A native checkbox rendered at 3x. The transform is anchored top-left and the
    // input is wrapped in a box of the resulting size, so the element occupies the
    // space it actually paints — without this the layout box stays 15px while the
    // control paints 45px, and neighbouring toggles never line up.
    const CHECKBOX_SCALE = 3;
    const CHECKBOX_BASE_SIZE = 15;
    const CHECKBOX_RENDERED_SIZE = CHECKBOX_BASE_SIZE * CHECKBOX_SCALE;

    // @param [Object] props
    // @option props [String] testid suffix for the toggle's data-testid attributes
    // @option props [Boolean] checked current state of the checkbox
    // @option props [Boolean] disabled whether the control is interactive
    // @option props [Boolean] muted greys the control out — used for the permanently
    //   unavailable Push Notifications toggle, not for transient disabled states
    // @option props [Function] onChange handler invoked with the change event
    // @return [JSX.Element] the sized checkbox control
    const renderCheckbox = ({ testid, checked, disabled, muted, onChange }) => (
        <div style={{ width: `${CHECKBOX_RENDERED_SIZE}px`, height: `${CHECKBOX_RENDERED_SIZE}px` }}>
            <input
                type="checkbox"
                data-testid={testid ? `communications-${testid}-toggle` : undefined}
                checked={!!checked}
                disabled={disabled}
                onChange={onChange}
                style={{
                    transform: `scale(${CHECKBOX_SCALE})`,
                    transformOrigin: 'top left',
                    width: `${CHECKBOX_BASE_SIZE}px`,
                    height: `${CHECKBOX_BASE_SIZE}px`,
                    padding: 0,
                    margin: 0,
                    ...(muted ? { backgroundColor: '#e0e0e0', cursor: 'not-allowed' } : {})
                }}
            />
        </div>
    );

    // Renders one notification-method toggle. The cell is a full-height flex column
    // with the checkbox pushed to the bottom, so labels that wrap onto two lines
    // ("Email Notifications") still align their box with the single-line ones.
    // @param [Object] props
    // @option props [String] testid suffix for the toggle's data-testid attributes
    // @option props [String] label visible caption above the checkbox
    // @option props [Boolean] checked current state of the checkbox
    // @option props [Boolean] disabled whether the control is interactive
    // @option props [Function] onChange handler invoked with the change event
    // @return [JSX.Element] the toggle cell
    const renderNotificationToggle = ({ testid, label, checked, disabled, muted, onChange }) => (
        <div className="flex flex-col h-full min-w-0" data-testid={`communications-${testid}`}>
            <label className="label font-medium text-gray-900">{label}</label>
            <div className="mt-auto pt-2">
                {renderCheckbox({ testid, checked, disabled, muted, onChange })}
            </div>
        </div>
    );

    // Renders one app-download settings toggle with its caption. Shared by the
    // reminder and automatic-deactivation switches so the markup lives in one
    // place; both are gated by the app_download_settings feature flag (CU-86caxajfv).
    // Wider than the notification-method toggles above because each carries a
    // sentence of explanation plus a recipients link.
    // @param [Object] props
    // @option props [String] feature feature key toggled on the company
    // @option props [String] testid suffix for the toggle's data-testid attributes
    // @option props [String] label visible caption above the checkbox
    // @option props [Boolean] checked current state of the checkbox
    // @option props [String] caption explanatory sentence below the checkbox
    // @option props [String] linkLabel text for the notification-subscriptions link
    // @return [JSX.Element] the toggle cell
    const renderAppDownloadToggle = ({ feature, testid, label, checked, caption, linkLabel }) => (
        <div className="w-full md:w-1/3 flex flex-col pr-4" data-testid={`communications-${testid}`}>
            <label className="label font-medium text-gray-900" style={{ whiteSpace: 'pre-wrap' }}>{label}</label>
            <div className="pt-2">
                {renderCheckbox({ testid, checked, onChange: () => handleChangeFunctionality(feature) })}
            </div>
            <p className="text-xs text-gray-500 mt-4" style={{ whiteSpace: 'pre-wrap' }}>
                {caption}{' '}
                <a href={`/companies/${selectedCompany.id}/notification_subscriptions`} className="text-blue-600 hover:underline">{linkLabel}</a>
            </p>
        </div>
    );

    // Renders one cancellation cost alert toggle with its caption. Saved with the
    // tab's Save button rather than on click, like the operations email.
    // @param [Object] props
    // @option props [String] field form field the toggle edits
    // @option props [String] testid suffix for the toggle's data-testid attributes
    // @option props [String] label visible caption above the checkbox
    // @option props [Boolean] checked current state of the checkbox
    // @option props [String] caption explanatory sentence beside the checkbox
    // @return [JSX.Element] the toggle cell
    const renderCostAlertToggle = ({ field, testid, label, checked, caption }) => (
        <div className="w-full md:w-1/2 flex flex-col pr-4 mb-4" data-testid={`communications-${testid}`}>
            <label className="label font-medium text-gray-900">{label}</label>
            <div className="flex items-start pt-2">
                {renderCheckbox({
                    testid,
                    checked,
                    onChange: (e) => setValue(field, e.target.checked, { shouldDirty: true })
                })}
                <p className="text-xs text-gray-500 ml-4 flex-1" style={{ whiteSpace: 'pre-wrap' }}>{caption}</p>
            </div>
        </div>
    );

    return (
        <div className="w-full p-4 flex flex-col">

            {/* Communications section */}
            <div className="communications flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Communications</h3>
                        <p className="text-sm text-gray-500">Communication methods</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col pr-5">
                    {/* Equal-height grid cells keep every checkbox on the same line even
                        when a label wraps onto two rows. Two columns on narrow screens,
                        four from the small breakpoint up. */}
                    <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-6 max-w-3xl">
                        {renderNotificationToggle({
                            testid: "sms-notifications",
                            label: "SMS Notifications",
                            checked: sms_functionality,
                            onChange: () => handleChangeFunctionality("sms")
                        })}

                        {renderNotificationToggle({
                            testid: "email-notifications",
                            label: "Email Notifications",
                            checked: email_functionality,
                            onChange: () => handleChangeFunctionality("emails")
                        })}

                        {renderNotificationToggle({
                            testid: "push-notifications",
                            label: "Push Notifications",
                            checked: false,
                            disabled: true,
                            muted: true,
                            onChange: () => {}
                        })}

                        {renderNotificationToggle({
                            testid: "requires-employee-id",
                            label: "Requires Employee ID",
                            checked: requireEmployeeID,
                            disabled: saving || !selectedCompany,
                            onChange: (e) => handleRequireEmployeeID(e.target.checked)
                        })}
                    </div>

                    <div className="w-full flex flex-col mt-8">
                        <label className="label font-medium text-gray-900">Operations Email</label>
                        <input
                            type="text"
                            value={operations_email}
                            onChange={(e) => setValue('operations_email', e.target.value, { shouldDirty: true })}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                            }}
                            className="input border text-sm py-1 rounded-lg w-full md:w-3/4 lg:w-5/6 border-gray-400 text-gray-900 px-2"
                        />
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* App downloads & employee activity — its own section rather than a
                sub-block of Communications, so the explanatory captions get the full
                content column. Gated by the app_download_settings feature flag (CU-86caxajfv). */}
            {selectedCompany?.app_download_settings_enabled && (
                <>
                    <div className="app_download_settings flex flex-wrap px-3 md:px-0" data-testid="communications-app-download-section">

                        <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            <div className="w-full pr-5">
                                <h3 className="text-lg font-medium text-gray-700">App downloads &amp; employee activity</h3>
                            </div>
                        </div>

                        <div className="w-full md:w-3/4 flex flex-wrap pr-5">
                            {renderAppDownloadToggle({
                                feature: "app_download_reminders",
                                testid: "app-download-reminders",
                                label: "App download reminders",
                                checked: app_download_reminders_functionality,
                                caption: "Invited employees who haven't logged in get a reminder 2 days after invite, then every 5 days (max 4). Selected managers/company admins get a weekly consolidated list.",
                                linkLabel: "Choose recipients"
                            })}

                            {renderAppDownloadToggle({
                                feature: "automatic_deactivation",
                                testid: "automatic-deactivation",
                                label: "Automatic deactivation",
                                checked: automatic_deactivation_functionality,
                                caption: "Inactive employee profiles are deactivated automatically on the next scheduled run.",
                                linkLabel: "Choose report recipients"
                            })}
                        </div>
                    </div>

                    <hr className="border border-gray-200 my-5" />
                </>
            )}

            {/* Cancellation cost alerts (CU-86cbawn2a) — Etapath admins only */}
            {canManageCostAlerts && (
                <>
                    <div className="cost_alerts flex flex-wrap px-3 md:px-0" data-testid="communications-cost-alerts-section">
                        <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            <div className="w-full pr-5">
                                <h3 className="text-lg font-medium text-gray-700">Alerts</h3>
                                <p className="text-sm text-gray-500">Cancellation cost alerts</p>
                            </div>
                        </div>

                        <div className="w-full md:w-3/4 flex flex-wrap pr-5">
                            {renderCostAlertToggle({
                                field: "late_cancellation_alert_enabled",
                                testid: "late-cancellation-alert",
                                label: "Late cancellations",
                                checked: late_cancellation_alert_enabled,
                                caption: `Cancellations made within ${selectedCompany?.cancellation_range ?? 2} hours of the trip are considered late. Late cancellations show a message telling the user the trip cost will still be charged.`
                            })}

                            {renderCostAlertToggle({
                                field: "post_cutoff_cancellation_alert_enabled",
                                testid: "post-cutoff-cancellation-alert",
                                label: "Post-cutoff cancellations",
                                checked: post_cutoff_cancellation_alert_enabled,
                                caption: `Cancellations made after the cut-off time${cutOffTimeLabel} on the trip day show a message telling the user a trip cost may still apply.`
                            })}
                        </div>
                    </div>

                    <hr className="border border-gray-200 my-5" />
                </>
            )}

            {/* Notification Center section */}
            <div className="notification_center">
                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Notification Center</h3>
                        <p className="text-sm text-gray-500">Sub details</p>
                    </div>
                </div>
                <div className="w-full md:w-1/3 flex flex-col mx-auto border-8 border-gray-400 p-4 h-32 text-xl text-gray-500 flex items-center justify-center" style={{ whiteSpace: 'pre-wrap' }}>
                    <p>Notification Center is coming soon!</p>
                </div>
            </div>

            <hr className="border border-gray-200 my-3 mb-16" />

            {/* Save and revert buttons */}
            <div className="relative pt-8">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        type="button"
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center relative cursor-pointer ${
                            isLoading ? "bg-gray-400" : "button"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={() => updateCompany()}
                        disabled={isLoading}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                        type="button"
                        className={`p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer ${
                            isDirty ? "bg-gray-400" : "bg-gray-500 hover:bg-gray-600"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={() => handleRevertChanges()}
                        disabled={isDirty == false}
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );

}

export default Communications;